import { Vue, Component } from 'vue-property-decorator';
import { DisplayEnum } from '@/types/enum';
import { SearchParams } from '@/types/product';
import { Mall, Brand, ContractedPartner, Mds, CustomProperty, ProductsSearch } from 'supporter';
import { namespace } from 'vuex-class';
import {
  getCurrency,
  centerWinOptions,
  productNameConvertor,
  toI18n,
  convertProductUri,
  getLocalStorageMalls,
} from '@/utils/commonUtils';

const termsModule = namespace('terms');

@Component
export default class ProductPopSearchDataMixin extends Vue {
  @termsModule.Mutation('SET_NCPLAYERISEXIST') public setPopIsExist!: (isExist: boolean) => void;
  @termsModule.Mutation('SET_NCPLAYERISSHOW') public setPopIsShow!: (isExist: boolean) => void;
  @termsModule.Mutation('SET_POPINFO') public setPopInfo!: (popInfo: {}) => void;
  @termsModule.Getter('getNcpLayerIsExist') public getNcpLayerIsExist!: boolean;
  @termsModule.Getter('getNcpLayerIsShow') public getNcpLayerIsShow!: boolean;

  public getStockCntColumnValue(
    representStockCnt: number,
    stockCnt: number,
    optionType: string,
    isSoldOut: boolean,
  ): string {
    const representCnt = representStockCnt;
    const isCombinationOption = optionType === 'COMBINATION';

    if (isSoldOut) {
      if (stockCnt === 0) {
        return this.toI18n('MSOLD_OUT'); //품절
      }
      return isCombinationOption ? `${this.toI18n('MSOLD_OUT')} (${stockCnt})` : `${stockCnt}`; //품절
    }

    return isCombinationOption ? `${representCnt} (${stockCnt})` : `${stockCnt}`;
  }

  public toI18n(needToI18nStr: string): string {
    return toI18n(this.$t('PRODUCT_POP_SEARCH_' + needToI18nStr));
  }

  /**
   * Old source $.urlParam params:
   * addAllConfirm
   * addConfirm
   * additionalDiscount
   * allBtn
   * callBackName
   * callbackParam
   * coupon
   * couponTarget
   * customProperty
   * description
   * displayCategoryInfo
   * emptyAlert
   * exceptedTarget
   * hideDesc
   * limit
   * noCheckMallProductNos
   * reverse
   * target
   * mallNo
   */
  public routeQuery = {
    addAllConfirm: '',
    addConfirm: '',
    confirmTexts: {
      addOne: this.toI18n('CONFIRM_MSG_ADDONE'),
      addAll: this.toI18n('CONFIRM_MSG_ADDALL'),
      delOne: this.toI18n('CONFIRM_MSG_DELONE'),
      delAll: this.toI18n('CONFIRM_MSG_DELALL'),
    },
    additionalDiscount: '',
    allBtn: '',
    callBackName: '',
    callbackParam: '',
    coupon: '',
    couponTarget: '',
    customProperty: '',
    description: '',
    displayCategoryInfo: '',
    emptyAlert: '',
    exceptedTarget: '',
    hideDesc: '',
    limit: '',
    noCheckMallProductNos: '',
    reverse: '',
    target: '',
    mallNo: '',
    addSelectedBtn: this.toI18n('TEXT_ADDSELECTEDBTN'),
    addAllBtn: this.toI18n('TEXT_ADDALLBTN'),
    delSelectedBtn: this.toI18n('TEXT_DELSELECTEDBTN'),
    delAllBtn: this.toI18n('TEXT_DELALLBTN'),
    cancelBtn: this.toI18n('TEXT_CANCELBTN'),
    btnType: '',
    page: '',
    allBtnWantOnePageData: '',
    noConfirm: '',
    pageType: '', // 상품평 예외 설정에서만 사용하는 alert message가 있어서 해당페이지 에서만 사용하기 위해 추가
    saleDisabledData: '',
  };
  public noCheckMallProductNos: number[] = [];
  public productDetailUrl: string = '';

  public customProperties: CustomProperty[] = [];

  public promotionYnDisable = false;
  public readonly ALERT_MESSAGES = {
    chooseMall: this.toI18n('PLEASE_SELECT_A_MALL_FIRST'), //쇼핑몰을 먼저 선택해주세요
  };

  public brandsList: Brand[] = [];
  public brandsInputVal: string = '';
  public brandsMenuIsShow = false;
  public isBrandDisable = false;

  public partnerList: ContractedPartner[] = [];
  public partnerInputVal: string = '';
  public partnerMenuIsShow = false;
  public isPartnerDisable = false;

  public adminList: Mds[] = [];
  public adminInputVal: string = '';
  public adminMenuIsShow = false;

  public categoiesCascade: any;
  public saleStatusTypeCheckBox: any;

  public isCommissionTypeDisabled = false;
  public isSupplyPriceViewShow = false;
  public isCommissionRateView = false;

  public searchType: DisplayEnum[] = [
    { value: 'MALL_PRODUCT_NO', label: this.toI18n('PRODUCT_NUMBER') }, //상품번호
    { value: 'PRODUCT_NAME', label: this.toI18n('PRODUCT_NAME') }, //상품명
    { value: 'PRODUCT_MANAGEMENT_CD', label: this.toI18n('PRODUCT_MANAGEMENT_CODE') }, //상품관리코드
    { value: 'PRODUCT_MANAGEMENT_CD_LIKE', label: this.toI18n('PRODUCT_MANAGEMENT_CODE_SEARCH') }, //상품관리코드|부분검색
  ];

  public applyStatusType: DisplayEnum[] = [
    { value: 'ALL', label: this.toI18n('APPROVAL_STATUS') }, //승인상태
    { value: 'REGISTRATION_READY', label: this.toI18n('WAITING_FOR_REGISTRATION') }, //등록대기
    { value: 'APPROVAL_READY', label: this.toI18n('WAITING_FOR_APPROVAL') }, //승인대기
    { value: 'APPROVAL_REJECTION', label: this.toI18n('REJECTION_OF_APPROVAL') }, //승인거부
    { value: 'SALE_AGREEMENT_READY', label: this.toI18n('WAITING_FOR_SALES_AGREEMENT') }, //판매합의대기
    { value: 'SALE_AGREEMENT_REJECTION', label: this.toI18n('REJECTION_OF_SALES_AGREEMENT') }, //판매합의거부
    { value: 'FINISHED', label: this.toI18n('APPROVAL_COMPLETED') }, //승인완료
    { value: 'AFTER_APPROVAL_READY', label: this.toI18n('WAITING_FOR_APPROVAL2') }, //수정 후 승인대기
    { value: 'AFTER_APPROVAL_REJECTION', label: this.toI18n('REJECT_APPROVAL') }, //수정 후 승인거부
  ];

  //stockRange.type: NONE:'0', EXIST:'1', LE: 1 5 10 50 100 (stockRange.stockCnt)
  public stockRange: DisplayEnum[] = [
    { value: '-1', label: this.toI18n('AVAILABILITY') }, //재고여부
    { value: '0', label: this.toI18n('NO_STOCK') }, //재고없음
    { value: '1', label: this.toI18n('IN_STOCK') }, //재고 있음
    { value: '5', label: this.toI18n('N5_OR_LESS') }, //5개 이하
    { value: '10', label: this.toI18n('N10_OR_LESS') }, //10개 이하
    { value: '50', label: this.toI18n('N50_OR_LESS') }, //50개 이하
    { value: '100', label: this.toI18n('N100_OR_LESS') }, //100개 이하
  ];

  //old api: promotionYn
  public allowsPromotion: DisplayEnum[] = [
    { value: 'ALL', label: this.toI18n('WHETHER_PROMOTION') }, //프로모션 여부
    { value: 'Y', label: this.toI18n('POSSIBLE') }, //가능
    { value: 'N', label: this.toI18n('IMPOSSIBLE') }, //불가
  ];
  //old api: frontDisplayYn
  public allowsFrontDisplay: DisplayEnum[] = [
    { value: 'ALL', label: this.toI18n('EXHIBITION_STATUS') }, //전시상태
    { value: 'Y', label: this.toI18n('DISPLAY_POSSIBLE') }, //전시가능
    { value: 'N', label: this.toI18n('NO_EXHIBITION') }, //전시안함
  ];
  public hasAdditionalDiscount: DisplayEnum[] = [
    { value: 'ALL', label: this.toI18n('ADDITIONAL_DISCOUNT') }, //추가할인여부
    { value: 'Y', label: this.toI18n('ADDITIONAL_DISCOUNT_AVAILABLE') }, //추가할인 있음
    { value: 'N', label: this.toI18n('NO_ADDITIONAL_DISCOUNT') }, //추가할인 없음
  ];
  public platformType: DisplayEnum[] = [
    { value: 'ALL', label: this.toI18n('PLATFORM_CLASSIFICATION') }, //플랫폼구분
    { value: 'PC', label: 'PC' },
    { value: 'MOBILE_APP', label: this.toI18n('MOBILE_APP') }, //모바일 앱
    { value: 'MOBILE_WEB', label: this.toI18n('MOBILE_WEB') }, //모바일 웹
  ];
  public saleMethodType: DisplayEnum[] = [
    { value: 'ALL', label: this.toI18n('SALEMETHODTYPE_ALL') }, //전체
    { value: 'PURCHASE', label: this.toI18n('BUY') }, //사입
    { value: 'CONSIGNMENT', label: this.toI18n('CONSIGNMENT') }, //위탁
  ];
  //cascade with commission.range.min & commission.range.max
  public commissionType: DisplayEnum[] = [
    { value: 'ALL', label: this.toI18n('ALL') }, //전체
    { value: 'RATE', label: this.toI18n('COMMISSION_RATE') }, //수수료율
    { value: 'SUPPLY_PRICE', label: this.toI18n('SUPPLY_PRICE') }, //공급가
  ];

  public isAllDisabled = false;
  public saleStatusData: string[] = ['ON_PRE_SALE', 'WAITING_SALE', 'ON_SALE', 'END_SALE'];
  public saleSettingStatusData: string[] = ['AVAILABLE_FOR_SALE', 'STOP_SELLING', 'PROHIBITION_SALE'];
  public saleDisabledData: string[] = [];

  public saleStatusList: DisplayEnum[] = [
    { value: 'ON_PRE_SALE', label: this.toI18n('ON_PRE_SALE') }, //'예약판매중'
    { value: 'WAITING_SALE', label: this.toI18n('WAITING_FOR_SALE') }, //판매대기
    { value: 'ON_SALE', label: this.toI18n('SALE') }, //판매중
    { value: 'END_SALE', label: this.toI18n('END_OF_SALE') }, //판매종료
  ];

  public saleSettingStatusList: DisplayEnum[] = [
    { value: 'AVAILABLE_FOR_SALE', label: this.toI18n('AVAILABLE_FOR_SALE') }, //판매가능
    { value: 'STOP_SELLING', label: this.toI18n('STOP_SELLING') }, //판매중지
    { value: 'PROHIBITION_SALE', label: this.toI18n('PROHIBITION_OF_SALE') }, //판매금지
  ];

  public incomingCheckedNos: number[] = [];

  public keywordTexthide: boolean = true;

  public popInfo = {
    // eslint-disable-next-line quotes
    title: `${this.toI18n('POP_INFO1')}`,
    subStyle: 'width:620px; max-height:800px; scroll-y:auto',
    noticeBox: `
<div class="notice_box no_mark">
  <p>${this.toI18n('POP_INFO2')}</p>
    <ul>
      <li>
          <div>${this.toI18n('POP_INFO3')}</div>
      </li>
      <li>
          <div>${this.toI18n('POP_INFO4')}</div>
      </li>
      <li>
          <div>${this.toI18n('POP_INFO5')}</div>
      </li>
    </ul>
</div>`,
  };

  public mallInfo: Mall[] = getLocalStorageMalls();
  public get USMallNos(): Mall[] {
    return this.mallInfo.filter((mall: Mall): any => mall.countryCode === 'US').map((mall): any => mall.mallNo) || [];
  }

  public beforeApprovalStatus = [
    'REGISTRATION_READY',
    'APPROVAL_READY',
    'APPROVAL_REJECTION',
    'SALE_AGREEMENT_READY',
    'SALE_AGREEMENT_REJECTION',
  ];

  public displayCategoryArray = [];

  public getProductDetailUrl({ value, row }: any): string {
    const { applyStatusType } = row;
    if (applyStatusType === 'REGISTRATION_READY') return row.mallProductNo;
    const uri = convertProductUri(this.productDetailUrl, value);
    return `<a target="_blank" href="${uri}">${value}</a>`;
  }
  public gridProps = {
    columns: [
      {
        header: this.toI18n('TSHOPPING_MALL'), //쇼핑몰
        name: 'mallName',
        align: 'center',
        minWidth: 100,
      },
      {
        header: this.toI18n('TPRODUCT_NUMBER'), //상품번호
        name: 'mallProductNo',
        align: 'center',
        minWidth: 100,
        formatter: this.getProductDetailUrl,
      },
      {
        header: this.toI18n('TPRODUCT_NAME'), //상품명
        name: 'productName',
        align: 'left',
        minWidth: 230,
        formatter: ({ value, row }: any): string => {
          return productNameConvertor(value, row);
        },
      },
      {
        header: this.toI18n('TPRODUCT_MANAGEMENT_CODE'), //상품관리코드
        name: 'productManagementCd',
        minWidth: 120,
        align: 'left',
      },
      {
        header: this.toI18n('TBRAND'), //브랜드
        name: 'brandName',
        align: 'center',
        minWidth: 100,
      },
      {
        header: this.toI18n('TPARTNER'), //파트너사
        name: 'partnerName',
        align: 'center',
        minWidth: 100,
      },
      {
        header: this.toI18n('TMANAGER'), //담당자
        name: 'adminName',
        align: 'center',
        minWidth: 100,
        formatter: ({ value }: any): string => {
          return value ? value : this.toI18n('TADMIN_NAME');
        },
      },
      {
        header: this.toI18n('TFEES'), //수수료
        name: 'commissionRate',
        align: 'right',
        minWidth: 80,
        formatter: ({ row }: any): string => {
          if (row.saleMethodType === 'PURCHASE' || row.commission.type === 'PURCHASE_PRICE') {
            return '-';
          }

          return `${row.commission.rate}%`;
        },
      },
      {
        header: this.toI18n('TSUPPLY_PRICE'), //공급가
        name: 'supplyPrice',
        align: 'right',
        minWidth: 80,
        formatter: ({ value, row }: any): string => {
          if (row.saleMethodType === 'PURCHASE') {
            return '-';
          }
          return getCurrency(value, this.USMallNos.includes(row.mallNo));
        },
      },
      {
        header: this.toI18n('TPURCHASE_PRICE'), //매입가
        name: 'purchasePrice',
        align: 'right',
        minWidth: 80,
        formatter: ({ row }: any): string => {
          if (row.saleMethodType === 'CONSIGNMENT') {
            return '-';
          }
          return getCurrency(row.supplyPrice, this.USMallNos.includes(row.mallNo));
        },
      },
      {
        header: this.toI18n('TAPPROVAL_STATUS'), //승인상태
        name: 'applyStatusType',
        minWidth: 120,
        align: 'center',
        ignored: true,
        //  applyStatusType's value:
        //  승인상태, Enum: [ REGISTRATION_READY: 등록대기,
        //  APPROVAL_READY: 승인대기,
        //  APPROVAL_REJECTION: 승인거부,
        //  SALE_AGREEMENT_READY: 판매합의대기,
        //  SALE_AGREEMENT_REJECTION: 판매합의거부,
        //  FINISHED: 승인완료,
        //  AFTER_APPROVAL_READY: 수정 후 승인대기,
        //  AFTER_APPROVAL_REJECTION: 수정 후 승인거부 ]
        formatter: ({ row }: any): any => {
          let text = '';
          switch (row.applyStatusType) {
            case 'REGISTRATION_READY':
              text = this.toI18n('TWAITING_FOR_REGISTRATION'); //등록대기
              break;
            case 'APPROVAL_READY':
              text = this.toI18n('TWAITING_FOR_APPROVAL'); //승인대기
              break;
            case 'APPROVAL_REJECTION':
              text = this.toI18n('TREJECTION_OF_APPROVAL'); //승인거부
              break;
            case 'SALE_AGREEMENT_READY':
              text = this.toI18n('TWAITING_FOR_SALES_AGREEMENT'); //판매합의대기
              break;
            case 'SALE_AGREEMENT_REJECTION':
              text = this.toI18n('TREJECTION_OF_SALES_AGREEMENT'); //판매합의거부
              break;
            case 'AFTER_APPROVAL_READY':
              text = this.toI18n('TWAITING_FOR_APPROVAL_AFTER_MODIFICATION'); //수정 후 승인대기
              break;
            case 'AFTER_APPROVAL_REJECTION':
              text = this.toI18n('TREJECT_APPROVAL_AFTER_MODIFICATION'); //수정 후 승인거부
              break;
            default:
              return this.toI18n('TAPPROVAL_COMPLETED'); //승인완료
          }

          return text;
        },
      },
      {
        header: this.toI18n('TSALES_STATUS'), //판매상태
        name: 'saleStatusType',
        minWidth: 160,
        align: 'center',
        formatter: (rowData: any): string => {
          const { value, row } = rowData;
          if (row.saleSettingStatusType === 'PROHIBITION_SALE') {
            return '-';
          }
          const saleStatus = this.saleStatusList
            .concat([{ value: 'PRE_APPROVAL_STATUS', label: this.toI18n('PRE_APPROVAL_STATUS') }])
            .find((item: any): boolean => item.value === value);
          return saleStatus ? (saleStatus.value === 'PRE_APPROVAL_STATUS' ? '-' : saleStatus.label) : '';
        },
      },
      {
        header: this.toI18n('TSALES_SETTING_STATUS'), //판매설정
        name: 'saleSettingStatusType',
        minWidth: 160,
        align: 'center',
        formatter: (rowData: any): string => {
          const { value } = rowData;
          const saleSettingStatus = this.saleSettingStatusList.find((item: any): boolean => item.value === value);
          return saleSettingStatus ? saleSettingStatus.label : '';
        },
      },
      {
        header: this.toI18n('EXHIBITION_STATUS'), //'전시상태',
        name: 'frontDisplayable',
        minWidth: 160,
        align: 'center',
        formatter: ({ value }: any): string => {
          let val = String(value);
          let text = '';
          switch (val) {
            case 'true':
              text = this.toI18n('TDISPLAY_POSSIBLE'); //전시가능
              break;
            case 'false':
              text = this.toI18n('TNO_EXHIBITION'); //전시안함
              break;
          }
          return text;
        },
      },
      {
        header: this.toI18n('TPRE_ORDER_PERIOD'), //예약판매기간
        name: 'reservationPeriod',
        minWidth: 250,
        align: 'center',
        formatter: ({ value }: any): string => {
          if (value && value.startYmdt && value.endYmdt) {
            return `${value.startYmdt} ~ ${value.endYmdt}`;
          } else {
            return '-';
          }
        },
      },
      {
        header: this.toI18n('TSALES_PERIOD'), //판매기간
        name: 'salePeriod',
        minWidth: 250,
        align: 'center',
        formatter: ({ value }: any): string => {
          if (value && value.startYmdt && value.endYmdt) {
            return `${value.startYmdt} ~ ${value.endYmdt}`;
          } else {
            return '-';
          }
        },
      },
      {
        header: this.toI18n('TDISCOUNT_APPLICABLE_PRICE'), //할인적용가
        // name: 'discountApplyPrice',
        name: 'appliedImmediateDiscountPrice',
        align: 'right',
        minWidth: 100,
        // appliedImmediateDiscountPrice
        formatter: ({ value, row }: any): string => {
          const val = getCurrency(value, row.countryCd === 'US');
          if (parseInt(val, 10) <= 0) {
            return '0';
          }
          return val;
        },
      },
      { header: this.toI18n('TADDITIONAL_DISCOUNT'), name: 'additionalDiscountAmount', align: 'right', minWidth: 100 }, //추가할인
      {
        header: this.toI18n('ADDITIONALDISCOUNTAPPLYPRICE'),
        name: 'appliedAdditionalDiscountPrice',
        align: 'right',
        minWidth: 100,
        formatter: ({ value, row }: any): string => {
          const val = getCurrency(value, row.countryCd === 'US');
          if (parseInt(val, 10) <= 0) {
            return '0';
          }
          return val;
        },
      },
      {
        header: this.toI18n('ADDITIONALDISCOUNTNAME'),
        name: 'additionalDiscountName',
        align: 'right',
        minWidth: 100,
      }, //추가할인 적용가
      {
        header: this.toI18n('TINVENTORY_QUANTITY'), //재고수량
        name: 'stockCnt',
        minWidth: 100,
        align: 'right',
        editOptions: { type: 'text', useViewMode: true },
        formatter: ({ row }: any): string => {
          // return row.productStock.safetyStockCnt;
          // return this.getStockCntColumnValue(row.productStock.representCnt, row.productStock.safetyStockCnt, row.optionType, row.isSoldOut);
          const totalCnt = row.productStock.totalCnt;
          if (totalCnt) {
            return totalCnt; //getCurrency(totalCnt, this.USMallNos.includes(row.mallNo));
          } else {
            return window.$t('PRODUCT_PRODUCTLIST_SOLD_OUT');
          }
        },
      },
      {
        header: this.toI18n('TAPPLY_PROMOTION'), //프로모션 적용
        name: 'canPromotion',
        minWidth: 100,
        align: 'center',
        formatter: ({ value }: any): string => {
          const currentValue = value ? 'Y' : 'N';

          return currentValue;
        },
      },
      {
        header: this.toI18n('TEXHIBITION_CATEGORY'), //전시카테고리
        name: 'displayCategoryNames',
        minWidth: 250,
        align: 'left',
        formatter: ({ value, row }: any): any => {
          if (!value || !value[0]) {
            return null;
          }

          //window open /products/{productNo}/display-categories
          const url = `/product/popup/displayCategoryInfo?mallProductNo=${row.mallProductNo}`;
          return `<a href="javascript:onclick=window.open('${url}','displayCategoryNamesWin','${centerWinOptions(
            520,
            210,
          )}')">${value[0]}</a>`;
        },
      },
    ],
    options: {
      rowHeaders: ['checkbox'],
      columnOptions: {
        minWidth: 50,
        frozenCount: 3,
        resizable: true,
      },
      bodyHeight: 480,
      minRowHeight: 28,
      rowHeight: 28,
      // virtualScrolling: true,
      isInitialRequest: false,
      useClientSort: false,
      heightResizable: true,
      pageOptions: {
        page: 1,
        totalCount: 0,
        perPage: 100,
      },
    },
  };
  public excelColumns = [
    { header: this.toI18n('TSHOPPING_MALL'), name: 'mallName' },
    { header: this.toI18n('TPRODUCT_NUMBER'), name: 'mallProductNo' },
    { header: this.toI18n('TPRODUCT_NAME'), name: 'productName' },
    { header: this.toI18n('TPRODUCT_MANAGEMENT_CODE'), name: 'productManagementCd' },
    { header: this.toI18n('TBRAND'), name: 'brandName' },
    { header: this.toI18n('TPARTNER'), name: 'partnerName' },
    { header: this.toI18n('TMANAGER'), name: 'adminName' },
    { header: this.toI18n('TFEES'), name: 'commissionRate' },
    // { header: this.toI18n('TSUPPLY_PRICE'), name: 'supplyPrice' },
    // { header: this.toI18n('TPURCHASE_PRICE'), name: 'purchasePrice' },
    { header: this.toI18n('TAPPROVAL_STATUS'), name: 'applyStatusType' },
    { header: this.toI18n('TSALES_STATUS'), name: 'saleStatusType' },
    { header: this.toI18n('TSALES_SETTING_STATUS'), name: 'saleSettingStatusType' },
    { header: this.toI18n('EXHIBITION_STATUS'), name: 'frontDisplayable' },
    // { header: this.toI18n('TPRE_ORDER_PERIOD'), name: 'reservationPeriod' },
    {
      header: this.toI18n('TSALES_PERIOD'),
      name: 'salePeriod',
      formatter: ({ value }: any): string => {
        if (value && value.startYmdt && value.endYmdt) {
          return `${value.startYmdt} ~ ${value.endYmdt}`;
        } else {
          return '-';
        }
      },
    },
    { header: this.toI18n('TDISCOUNT_APPLICABLE_PRICE'), name: 'appliedImmediateDiscountPrice' },
    { header: this.toI18n('TADDITIONAL_DISCOUNT'), name: 'additionalDiscountAmount' },
    { header: this.toI18n('ADDITIONALDISCOUNTAPPLYPRICE'), name: 'appliedAdditionalDiscountPrice' },
    { header: this.toI18n('ADDITIONALDISCOUNTNAME'), name: 'additionalDiscountName' },
    { header: this.toI18n('TINVENTORY_QUANTITY'), name: 'stockCnt' },
    {
      header: this.toI18n('TAPPLY_PROMOTION'),
      name: 'canPromotion',
      formatter: ({ value }: any): string => {
        const currentValue = value ? 'Y' : 'N';

        return currentValue;
      },
    },
    { header: this.toI18n('TEXHIBITION_CATEGORY'), name: 'displayCategoryNames' },
  ];

  public contentBottomOption = {
    name: 'product_search_grid',
    onLoadSearch: false,
    haveConfigBtn: false,
    haveExcelDownBtn: false,
    pageSize: '100',
  };

  public searchRespones: ProductsSearch = {
    contents: [],
    totalPage: 0,
    totalCount: 0,
  };

  public invalidKeywordSwitch = false;
  public invalidKeywordText = '';
  public invalidKeywordClass = '';

  public searchParams: SearchParams = {
    mallNos: '',
    'keywordInfo.type': 'MALL_PRODUCT_NO',
    'keywordInfo.keywords': '',
    brandNo: -100,
    partnerNo: -100,
    adminNo: -100,
    applyStatusType: 'ALL',
    'saleStatus.isAll': false,
    'saleStatus.types': '',
    'saleSettingStatus.isAll': false,
    'saleSettingStatus.types': '',
    'categoryCondition.type': 'DISPLAY',
    'categoryCondition.depth': '',
    'categoryCondition.no': '',
    'stockRange.type': 'ALL',
    // 'stockRange.stockCnt': -1,
    allowsPromotion: 'ALL',
    allowsFrontDisplay: 'ALL',
    hasAdditionalDiscount: 'ALL',
    platformType: 'ALL',
    saleMethodType: 'ALL',
    'commission.type': 'ALL',
    'commission.range.min': 0,
    'commission.range.max': 100,
    customPropertyValueNos: '',
    'periodInfo.type': 'REGISTER_DATE',
    'periodInfo.period.startYmdt': '1970-01-01 00:00:00',
    'periodInfo.period.endYmdt': '2999-12-31 23:59:59',
    page: 1,
    size: 100,
  };
  public searchParamsResetData = { ...this.searchParams };
}
